import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FiArrowUpRight, FiCheck, FiCompass, FiLayers, FiZap } from "react-icons/fi";

import { AppWrap, MotionWrap } from "../../wrapper";
import { client, urlFor } from "../../client";
import "./About.scss";

const fallbackServices = [
  {
    title: "Interface Strategy",
    description: "I translate product goals into clear UI flows, component structure, and launch-ready interaction patterns.",
    imgUrl: null,
  },
  {
    title: "Frontend Engineering",
    description: "I build fast interfaces with resilient layouts, reusable modules, and maintainable interaction patterns.",
    imgUrl: null,
  },
  {
    title: "Experience Polish",
    description: "I refine motion, responsiveness, accessibility, and performance so the interface feels deliberate at every breakpoint.",
    imgUrl: null,
  },
];

const includes = [
  "Clean responsive implementation",
  "Content-ready frontend structure",
  "Launch polish across real screens",
];

const principles = [
  { icon: <FiCompass />, title: "Product clarity", text: "Every section earns its space and guides users toward the next action." },
  { icon: <FiLayers />, title: "Scalable systems", text: "Components are built to survive future content, breakpoints, and iteration." },
  { icon: <FiZap />, title: "Fast by default", text: "Visual polish is paired with lightweight implementation and clean rendering." },
];

const TOOL_LANGUAGE_RE = /\b(react|reactjs|react js|javascript|js|typescript|html|css|redux|sanity|cms|node|express|mongodb|mongo)\b/i;

const getServiceCopy = (service, index) => {
  const fallback = fallbackServices[index] || fallbackServices[0];
  const title = service.title || fallback.title;
  const description = service.description || fallback.description;

  return {
    ...service,
    title: TOOL_LANGUAGE_RE.test(title) ? fallback.title : title,
    description: TOOL_LANGUAGE_RE.test(description) ? fallback.description : description,
  };
};

const About = () => {
  const [abouts, setAbouts] = useState([]);

  useEffect(() => {
    client.fetch('*[_type == "abouts"]').then((data) => setAbouts(data || []));
  }, []);

  const services = abouts.length ? abouts : fallbackServices;

  return (
    <div className="abt">
      <div className="app__section-head">
        <motion.div
          className="app__section-title"
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
        >
          <span className="app__kicker">
            <em>01</em>Process
          </span>
          <h2>
            Work that feels <em>planned</em> &mdash; never patched together.
          </h2>
        </motion.div>

        <motion.p
          className="app__section-lede"
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
        >
          I help founders, agencies, and product teams turn visual direction
          into polished frontend that loads fast, scales cleanly, and makes the
          business feel more credible.
        </motion.p>
      </div>

      <div className="abt__layout">
        <motion.aside
          className="abt__aside"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <blockquote className="abt__quote">
            Quality lives in the details <em>no one has to notice.</em>
          </blockquote>

          <div className="abt__proof">
            <span className="abt__proof-title">Every engagement includes</span>
            <ul>
              {includes.map((item) => (
                <li key={item}>
                  <i>
                    <FiCheck />
                  </i>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </motion.aside>

        <div className="abt__services">
          {services.slice(0, 3).map((service, index) => {
            const serviceCopy = getServiceCopy(service, index);

            return (
              <motion.article
                className="abt__service"
                key={`${serviceCopy.title}-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, amount: 0.2 }}
              >
                <span className="abt__service-num">{String(index + 1).padStart(2, "0")}</span>
                <div className="abt__service-copy">
                  <h3>{serviceCopy.title}</h3>
                  <p>{serviceCopy.description}</p>
                </div>
                {serviceCopy.imgUrl && (
                  <span className="abt__service-media">
                    <img src={urlFor(serviceCopy.imgUrl)} alt={serviceCopy.title} />
                  </span>
                )}
                <span className="abt__service-arrow" aria-hidden="true">
                  <FiArrowUpRight />
                </span>
              </motion.article>
            );
          })}
        </div>
      </div>

      <div className="abt__principles">
        {principles.map((item, index) => (
          <motion.div
            className="abt__principle"
            key={item.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <span className="abt__principle-icon">{item.icon}</span>
            <h4>{item.title}</h4>
            <p>{item.text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AppWrap(
  MotionWrap(About, "app__about"),
  "about",
  "app__band--stone"
);
